import {
  AccountCancellationPolicyDto,
  CANCELLATION_POLICY_TEXT_MAX_LENGTH,
  CANCELLATION_POLICY_WINDOW_LIMITS,
  CancellationPolicyFieldsDto,
  CancellationPolicyWindowUnit,
} from '@shared/core/cancellation-policy'
import type {
  CancellationPolicyFormErrors,
  CancellationPolicyFormValues,
} from '../types/cancellation-policy-form.types'

const UNIT_SINGULAR: Record<CancellationPolicyWindowUnit, string> = { hours: 'hour', days: 'day' }

export function formatPolicyWindow(value: number, unit: CancellationPolicyWindowUnit): string {
  return `${value} ${value === 1 ? UNIT_SINGULAR[unit] : unit}`
}

export function buildDefaultPolicyText(value: number, unit: CancellationPolicyWindowUnit): string {
  return `Please cancel at least ${formatPolicyWindow(value, unit)} before your appointment. Late cancellations may be charged.`
}

/** Whole number inside the unit's range, else null. */
export function parsePolicyWindowValue(raw: string, unit: CancellationPolicyWindowUnit): number | null {
  const trimmed = String(raw ?? '').trim()
  if (!/^\d+$/.test(trimmed)) return null
  const value = Number(trimmed)
  const { min, max } = CANCELLATION_POLICY_WINDOW_LIMITS[unit]
  return value >= min && value <= max ? value : null
}

export function validatePolicyForm(values: CancellationPolicyFormValues): CancellationPolicyFormErrors {
  const errors: CancellationPolicyFormErrors = {}
  if (parsePolicyWindowValue(values.notice_value, values.notice_unit) === null) {
    const { min, max } = CANCELLATION_POLICY_WINDOW_LIMITS[values.notice_unit]
    errors.notice_value = `Enter a whole number of ${values.notice_unit} from ${min} to ${max}.`
  }
  if (values.policy_text.length > CANCELLATION_POLICY_TEXT_MAX_LENGTH) {
    errors.policy_text = `Policy text can be at most ${CANCELLATION_POLICY_TEXT_MAX_LENGTH} characters.`
  }
  return errors
}

export function toPolicyFormValues(dto: AccountCancellationPolicyDto): CancellationPolicyFormValues {
  return {
    enabled: dto.enabled,
    notice_value: String(dto.notice_value),
    notice_unit: dto.notice_unit,
    allow_late_cancel: dto.allow_late_cancel,
    allow_reschedule_in_window: dto.allow_reschedule_in_window,
    policy_text: dto.policy_text,
    policy_text_edited: dto.policy_text_edited,
  }
}

export function toAccountPolicyDto(values: CancellationPolicyFormValues): AccountCancellationPolicyDto {
  return {
    enabled: values.enabled,
    notice_value: Number(values.notice_value.trim()),
    notice_unit: values.notice_unit,
    allow_late_cancel: values.allow_late_cancel,
    allow_reschedule_in_window: values.allow_reschedule_in_window,
    policy_text: values.policy_text,
    policy_text_edited: values.policy_text_edited,
  }
}

const COMPARED_FIELDS = [
  'enabled',
  'notice_value',
  'notice_unit',
  'allow_late_cancel',
  'allow_reschedule_in_window',
  'policy_text',
] as const

export type ComparedPolicyField = (typeof COMPARED_FIELDS)[number]

/** Fields whose form value differs from the saved row; `policy_text_edited` is bookkeeping, not policy. */
export function changedPolicyFields(
  values: CancellationPolicyFormValues,
  saved: AccountCancellationPolicyDto,
): ComparedPolicyField[] {
  const current = toAccountPolicyDto(values)
  return COMPARED_FIELDS.filter((field) => current[field] !== saved[field])
}

export function describeCustomPolicy(policy: CancellationPolicyFieldsDto | undefined): string {
  if (!policy) return 'Custom'
  const lateCancels = policy.allow_late_cancel ? 'late cancels allowed' : 'late cancels blocked'
  return `Custom: ${formatPolicyWindow(policy.notice_value, policy.notice_unit)}, ${lateCancels}`
}
